import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { approvalsApi } from '../services/routinesApi';
import { rbacApi } from '../services/rbacApi';
import {
  Inbox, Loader2, CheckCircle2, ExternalLink, UserPlus, Clock, X, ArrowRight,
  Filter, ArrowDownWideNarrow, Flame, MessageSquare, Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const stateStyle = (color) => color
  ? { backgroundColor: `${color}18`, color, borderColor: `${color}55` }
  : {};

const Approvals = () => {
  const navigate = useNavigate();
  const [scope, setScope] = useState('awaiting');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ awaiting: 0, assigned: 0 });
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [sort, setSort] = useState('waiting_desc');
  const [overdueDays, setOverdueDays] = useState(() => {
    const v = parseInt(localStorage.getItem('approvals_overdue_days'), 10);
    return Number.isFinite(v) && v >= 0 ? v : 7;
  });
  const [acting, setActing] = useState(null);
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState('');
  const [assignFor, setAssignFor] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignUser, setAssignUser] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [openNotes, setOpenNotes] = useState(null);
  const [notesByKey, setNotesByKey] = useState({});
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const noteKey = (it) => `${it.doctype}:${it.record_id}`;

  const toggleNotes = async (it) => {
    const key = noteKey(it);
    if (openNotes === key) { setOpenNotes(null); return; }
    setOpenNotes(key); setNoteText('');
    if (!notesByKey[key]) {
      setNotesLoading(true);
      try {
        const r = await approvalsApi.listComments(it.doctype, it.record_id);
        setNotesByKey((m) => ({ ...m, [key]: r.data.items || [] }));
      } catch { setNotesByKey((m) => ({ ...m, [key]: [] })); }
      finally { setNotesLoading(false); }
    }
  };

  const submitNote = async (it) => {
    const key = noteKey(it);
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const r = await approvalsApi.addComment({
        doctype: it.doctype, record_id: it.record_id, text: noteText.trim(),
      });
      setNotesByKey((m) => ({ ...m, [key]: [...(m[key] || []), r.data] }));
      setItems((list) => list.map((x) => (noteKey(x) === key
        ? { ...x, comment_count: (x.comment_count || 0) + 1 } : x)));
      setNoteText('');
      toast.success('Note added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add note'); }
    finally { setSavingNote(false); }
  };

  const refresh = useCallback((sc, mod, srt) => {
    setLoading(true);
    approvalsApi.inbox(sc, mod, srt).then((r) => {
      setItems(r.data.items || []);
      setModules(r.data.modules || []);
    }).catch(() => setItems([])).finally(() => setLoading(false));
    approvalsApi.count().then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(scope, moduleFilter, sort); }, [scope, moduleFilter, sort, refresh]);
  useEffect(() => { rbacApi.listUsers().then((r) => setUsers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem('approvals_overdue_days', String(overdueDays)); }, [overdueDays]);

  const isOverdue = (it) => typeof it.waiting_days === 'number' && it.waiting_days >= overdueDays;
  const overdueCount = items.filter(isOverdue).length;

  const doAct = async (item, action, cmt) => {
    setActing(`${item.doctype}:${item.record_id}:${action.to_state}`);
    try {
      await approvalsApi.act({
        doctype: item.doctype, record_id: item.record_id,
        to_state: action.to_state, comment: cmt || undefined,
      });
      toast.success(`${action.action_label} done`);
      setCommentFor(null); setComment('');
      refresh(scope, moduleFilter, sort);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setActing(null); }
  };

  const onAction = (item, action) => {
    if (action.requires_comment) { setCommentFor({ item, action }); setComment(''); }
    else doAct(item, action);
  };

  const submitAssign = async () => {
    if (!assignUser) { toast.error('Pick a user'); return; }
    try {
      await approvalsApi.assign({
        doctype: assignFor.doctype, record_id: assignFor.record_id,
        title: assignFor.title, current_state: assignFor.current_state,
        assigned_to: assignUser, note: assignNote || undefined,
      });
      toast.success('Assigned');
      setAssignFor(null); setAssignUser(''); setAssignNote('');
      approvalsApi.count().then((r) => setCounts(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
  };

  const removeAssignment = async (id) => {
    try { await approvalsApi.unassign(id); toast.success('Removed'); refresh(scope, moduleFilter, sort); }
    catch { toast.error('Failed'); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="approvals-page">
        <div className="flex items-center gap-3 mb-1">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-600 text-white"><Inbox className="h-5 w-5" /></div>
          <h1 className="text-2xl font-bold text-gray-900">My Approvals</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">Records across every module waiting on your action.</p>

        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-6">
          <button onClick={() => setScope('awaiting')} data-testid="approvals-tab-awaiting"
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${scope === 'awaiting' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}>
            Awaiting My Action
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">{counts.awaiting}</span>
          </button>
          <button onClick={() => setScope('assigned')} data-testid="approvals-tab-assigned"
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${scope === 'assigned' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}>
            Assigned to Me
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">{counts.assigned}</span>
          </button>
        </div>

        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-gray-400" />
            <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
              data-testid="approvals-module-filter"
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-200">
              <option value="all">All modules</option>
              {modules.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowDownWideNarrow className="h-3.5 w-3.5 text-gray-400" />
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              data-testid="approvals-sort"
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-200">
              <option value="waiting_desc">Longest waiting first</option>
              <option value="waiting_asc">Newest first</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5" data-testid="approvals-overdue-control">
            <Flame className="h-3.5 w-3.5 text-red-400" />
            <span className="text-xs text-gray-500">Overdue after</span>
            <input type="number" min="0" value={overdueDays}
              onChange={(e) => setOverdueDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
              data-testid="approvals-overdue-days"
              className="w-16 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-red-200" />
            <span className="text-xs text-gray-500">days</span>
          </div>
          <span className="text-xs text-gray-400">{items.length} item(s)</span>
          {overdueCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600" data-testid="approvals-overdue-count">
              <Flame className="h-3.5 w-3.5" /> {overdueCount} overdue
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 p-12 text-center" data-testid="approvals-empty">
            <CheckCircle2 className="h-10 w-10 text-green-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{scope === 'awaiting' ? "You're all caught up. Nothing needs your action." : 'Nothing is assigned to you.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it, i) => {
              const overdue = isOverdue(it);
              return (
              <div key={`${it.doctype}-${it.record_id}-${i}`} data-testid={`approval-item-${i}`}
                data-overdue={overdue ? 'true' : 'false'}
                className={`rounded-xl border p-4 transition-shadow hover:shadow-sm border-l-4 ${overdue ? 'border-red-200 border-l-red-500 bg-red-50/40' : 'border-gray-200 border-l-transparent bg-white'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{it.module || it.doctype}</span>
                      <span className="font-semibold text-gray-900">{it.record_id}</span>
                      {it.current_state && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full border" style={stateStyle(it.current_state_color)}>
                          {it.current_state_label || it.current_state}
                        </span>
                      )}
                      {typeof it.waiting_days === 'number' && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${overdue ? 'text-red-600' : 'text-gray-500'}`} data-testid={`approval-waiting-${i}`}>
                          <Clock className="h-3 w-3" /> {it.waiting_days}d waiting
                        </span>
                      )}
                      {overdue && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 rounded-full px-2 py-0.5" data-testid={`approval-overdue-badge-${i}`}>
                          <Flame className="h-3 w-3" /> Overdue
                        </span>
                      )}
                    </div>
                    {it.title && it.title !== it.record_id && <p className="text-sm text-gray-500 mt-0.5 truncate">{it.title}</p>}
                    {scope === 'assigned' && it.assigned_by && (
                      <p className="text-xs text-gray-400 mt-1">Assigned by {it.assigned_by}{it.note ? ` — ${it.note}` : ''}</p>
                    )}
                  </div>
                  <button onClick={() => it.view_path && navigate(it.view_path)} data-testid={`approval-open-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-orange-600 shrink-0">
                    Open <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {(it.actions || []).map((a, ai) => {
                    const busy = acting === `${it.doctype}:${it.record_id}:${a.to_state}`;
                    return (
                      <button key={ai} onClick={() => onAction(it, a)} disabled={busy}
                        data-testid={`approval-action-${i}-${ai}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-orange-700 disabled:opacity-60 transition-colors">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                        {a.action_label}
                      </button>
                    );
                  })}
                  {scope === 'awaiting' && (
                    <button onClick={() => { setAssignFor(it); setAssignUser(''); setAssignNote(''); }}
                      data-testid={`approval-assign-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-700 px-3 py-1.5 text-sm font-medium hover:bg-gray-50">
                      <UserPlus className="h-3.5 w-3.5" /> Assign
                    </button>
                  )}
                  {scope === 'assigned' && (
                    <button onClick={() => removeAssignment(it.assignment_id)} data-testid={`approval-remove-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-500 px-3 py-1.5 text-sm font-medium hover:bg-red-50 hover:text-red-600">
                      <X className="h-3.5 w-3.5" /> Remove
                    </button>
                  )}
                  <button onClick={() => toggleNotes(it)} data-testid={`approval-notes-toggle-${i}`}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${openNotes === noteKey(it) ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    <MessageSquare className="h-3.5 w-3.5" /> Notes
                    {(it.comment_count || 0) > 0 && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700" data-testid={`approval-notes-count-${i}`}>{it.comment_count}</span>
                    )}
                  </button>
                </div>

                {openNotes === noteKey(it) && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/70 p-3" data-testid={`approval-notes-panel-${i}`}>
                    {notesLoading && !notesByKey[noteKey(it)] ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading notes…</div>
                    ) : (notesByKey[noteKey(it)] || []).length === 0 ? (
                      <p className="text-xs text-gray-400 py-1" data-testid={`approval-notes-empty-${i}`}>No notes yet. Add the first one below.</p>
                    ) : (
                      <div className="space-y-2 mb-3">
                        {(notesByKey[noteKey(it)] || []).map((c) => (
                          <div key={c.comment_id} className="rounded-md bg-white border border-gray-100 px-3 py-2" data-testid={`approval-note-${i}-${c.comment_id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-semibold text-gray-700">{c.author_name || c.author}</span>
                              <span className="text-[11px] text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap break-words">{c.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note for the next approver…" rows={2}
                        data-testid={`approval-note-input-${i}`}
                        className="flex-1 text-sm bg-white" />
                      <Button onClick={() => submitNote(it)} disabled={!noteText.trim() || savingNote}
                        data-testid={`approval-note-submit-${i}`}
                        className="bg-orange-600 hover:bg-orange-700 shrink-0">
                        {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* comment dialog */}
      <Dialog open={!!commentFor} onOpenChange={(o) => !o && setCommentFor(null)}>
        <DialogContent data-testid="approval-comment-dialog">
          <DialogHeader><DialogTitle>{commentFor?.action?.action_label} — comment required</DialogTitle></DialogHeader>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Enter a comment…"
            data-testid="approval-comment-input" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentFor(null)}>Cancel</Button>
            <Button disabled={!comment.trim()} onClick={() => doAct(commentFor.item, commentFor.action, comment)}
              data-testid="approval-comment-submit" className="bg-orange-600 hover:bg-orange-700">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent data-testid="approval-assign-dialog">
          <DialogHeader><DialogTitle>Assign {assignFor?.record_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Assign to</label>
              <select value={assignUser} onChange={(e) => setAssignUser(e.target.value)}
                data-testid="approval-assign-user"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select a user…</option>
                {users.map((u) => <option key={u.username || u.id} value={u.username}>{u.full_name || u.username} ({u.username})</option>)}
              </select>
            </div>
            <Textarea value={assignNote} onChange={(e) => setAssignNote(e.target.value)}
              placeholder="Optional note…" data-testid="approval-assign-note" rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button onClick={submitAssign} data-testid="approval-assign-submit" className="bg-orange-600 hover:bg-orange-700">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Approvals;
